import React, { useCallback, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Button, ButtonProps, Stack } from "@mui/material";
import { FiltersChip } from "components";
import { MeasureDataTableFilters } from "store/types";

import { FiltersDrawer } from "../../Filters-drawer/FiltersDrawer";
import { SendActivityEmail } from "../SendActivityEmail/SendActivityEmail";

const buttonStyles: ButtonProps["sx"] = {
  height: "26px",
  fontSize: "15px",
  textTransform: "none",
  fontWeight: "400"
};

interface Props {
  filters: MeasureDataTableFilters;
  setFilters: (data: Partial<MeasureDataTableFilters> | null) => void;
  disabled?: boolean;
}

const limitTags = 4;

type Field = { key: keyof MonitoringDataFiltersArrs; title: string };

const dumbFilterTitles: Record<keyof MonitoringDataFiltersArrs, Field> = {
  measures: { title: "Measures", key: "measures" },
  // rounds: { title: "Rounds", key: "rounds" },
  enteredBy: { title: "Entered By", key: "enteredBy" },
  investors: { title: "Investors", key: "investors" }
};

type MonitoringDataFiltersArrs = Pick<MeasureDataTableFilters, "measures" | "enteredBy" | "investors">;
type OnRemoveOption = {
  field: Field;
  option: {
    id: number;
    name: string;
  };
};

export const DataFilters: React.FC<Props> = ({ filters, setFilters, disabled }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const isFiltered = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { hideDeleted, ...rest } = filters;
    return Object.values(rest).some((item) => (Array.isArray(item) ? item.length : !!item));
  }, [filters]);

  const pickFilters: MonitoringDataFiltersArrs = useMemo(() => {
    const { measures, investors, enteredBy } = filters;
    return { measures, investors, enteredBy };
  }, [filters]);

  const onRemoveField = useCallback(
    (field: Field) => {
      setFilters({ ...filters, [field.key]: [] });
    },
    [filters, setFilters]
  );

  const onRemoveOption = useCallback(
    ({ field, option }: OnRemoveOption) => {
      setFilters({ ...filters, [field.key]: filters[field.key].filter((item) => item.id !== option.id) });
    },
    [filters, setFilters]
  );

  return (
    <Stack
      direction="row"
      sx={{ justifyContent: "space-between", alignItems: "center", height: 26, gap: "10px", width: "100%" }}
    >
      <Stack direction="row" justifyContent="space-between" sx={{ width: "100%", marginRight: "20px" }}>
        <Stack direction="row" sx={{ gap: "14px", alignItems: "center" }}>
          <Button
            disabled={disabled}
            sx={{ ...buttonStyles, backgroundColor: isFiltered ? "#dae0e2" : "transparent" }}
            onClick={() => setIsDrawerOpen(true)}
            variant="outlined"
            size="sm"
            startIcon={<FilterAltIcon />}
          >
            Filters
          </Button>
          {Object.keys(pickFilters)
            .slice(0 - limitTags)
            .map((field) => (
              <FiltersChip
                key={field}
                field={dumbFilterTitles[field as keyof MonitoringDataFiltersArrs]}
                options={filters[field as keyof MonitoringDataFiltersArrs]}
                renderOption={(option) => option.name}
                removeFieldOption={onRemoveOption}
                onRemove={onRemoveField}
                renderField={(field) => field.title}
              />
            ))}
          {isFiltered && (
            <Button disabled={disabled} onClick={() => setFilters(null)} startIcon={<CloseIcon className="icon" />}>
              Clear All Filters
            </Button>
          )}
        </Stack>
        <SendActivityEmail />
      </Stack>

      <FiltersDrawer
        filters={filters}
        setFilters={setFilters}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </Stack>
  );
};
